import type { ReactNode, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import { TbArrowUpRight } from 'react-icons/tb'
import clsx from 'clsx'
import type { StackProps } from '~/components-v2'
import { Stack } from '~/components-v2'
import { useClickAnyWhere, useHover } from 'usehooks-ts'
import { JSX } from 'react'
import { SquaredIcon } from '~/components-v2/squaredIcon/squaredIcon'
import { DrawerArrow, LinkArrowIcon } from '~/components-v2/icons'

type NavCardContentProps = Pick<
  StackProps,
  'className' | 'gap' | 'row' | 'wrap'
>

type NavCardProps = {
  className?: string
  children?: ReactNode
  title: string | JSX.Element
  subTitle?: string | JSX.Element
  icon?: JSX.Element | null
  contentProps?: NavCardContentProps
  externalLink?: boolean
  to?: string
}
export const NavCard = ({
  className,
  title,
  icon,
  subTitle,
  children,
  externalLink,
  to,
}: NavCardProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [flipDrawer, setFlipDrawer] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const isTouchDevice =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: coarse)').matches
  const isHover = useHover(ref as RefObject<HTMLDivElement>)
  const isParentItem = Boolean(children)

  const [prevIsHover, setPrevIsHover] = useState(isHover)
  if (isHover !== prevIsHover) {
    setPrevIsHover(isHover)
    if (!isTouchDevice) setIsOpen(isHover)
  }

  if (!isOpen && flipDrawer) setFlipDrawer(false)

  useClickAnyWhere((event) => {
    if (!isOpen) return
    if (!ref.current) return
    if (ref.current.contains(event.target as Node)) return
    setIsOpen(false)
  })

  useEffect(() => {
    if (!isParentItem || !isOpen) return
    if (typeof window === 'undefined') return

    const updateFlip = () => {
      const drawer = drawerRef.current
      if (!drawer) return
      const rect = drawer.getBoundingClientRect()
      const overflowRight = rect.right > window.innerWidth
      const overflowLeft = rect.left < 0

      if (overflowRight && !overflowLeft) {
        setFlipDrawer(true)
      } else if (!overflowRight) {
        setFlipDrawer(false)
      }
    }

    const raf = window.requestAnimationFrame(updateFlip)
    window.addEventListener('resize', updateFlip)

    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('resize', updateFlip)
    }
  }, [isOpen, isParentItem])

  return (
    <>
      <Stack row className={clsx(className)} gap={5}>
        {externalLink ? (
          <a
            target="_blank"
            rel="noreferrer"
            href={to}
            className={clsx(
              subTitle ? 'items-start' : 'items-center',
              'nav-card-content external-link-card',
            )}
          >
            <span className="details">
              <span className="summary">
                {icon && <SquaredIcon icon={icon} />}
                <article>
                  <span>
                    <p className="nav-m">{title}</p>
                    {externalLink && <TbArrowUpRight />}
                  </span>
                  {subTitle && <p>{subTitle}</p>}
                </article>

                {children && <DrawerArrow className="nav-drawer-arrow" />}
                {isParentItem && (
                  <Stack
                    className={clsx('nav-drawer', flipDrawer && 'flip')}
                    ref={drawerRef}
                  >
                    {children}
                  </Stack>
                )}
              </span>
            </span>
          </a>
        ) : (
          <div
            className={clsx(!to && 'labelOnly', isOpen && 'is-open')}
            ref={ref}
          >
            <div
              className={clsx(
                subTitle ? 'items-start' : 'items-center',
                'nav-card-content',
              )}
              ref={triggerRef}
              onClick={() => {
                if (isTouchDevice) setIsOpen((prev) => !prev)
              }}
            >
              {icon && <SquaredIcon icon={icon} />}
              <article>
                <span className="nav-card-heading">
                  <p className="nav-m">{title}</p>
                  <LinkArrowIcon />
                </span>
                {subTitle && <p className="subtitle">{subTitle}</p>}
              </article>
              {children && <DrawerArrow className="nav-drawer-arrow" />}
            </div>
            {isParentItem && (
              <Stack
                className={clsx('nav-drawer', flipDrawer && 'flip')}
                ref={drawerRef}
              >
                {children}
              </Stack>
            )}
          </div>
        )}
      </Stack>
    </>
  )
}
